import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react';
type VerificationCodeInputProps = {
  autoFocus?: boolean;
  disabled?: boolean;
  length?: number;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void | Promise<void>;
  value: string;
};
const sanitizeCode = (value: string, length: number) => value.replace(/\D/g, '').slice(0, length);
export const VerificationCodeInput = ({
  autoFocus = false,
  disabled = false,
  length = 6,
  onChange,
  onComplete,
  value
}: VerificationCodeInputProps) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const verificationPending = useRef(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const isDisabled = disabled || isVerifying;
  const normalizedValue = sanitizeCode(value, length);
  useEffect(() => {
    if (autoFocus && !isDisabled) inputRefs.current[0]?.focus();
  }, [autoFocus, isDisabled]);
  const updateValue = async (nextValue: string) => {
    if (disabled || verificationPending.current) return;
    const sanitizedValue = sanitizeCode(nextValue, length);
    onChange(sanitizedValue);
    if (sanitizedValue.length !== length || !onComplete) return;
    verificationPending.current = true;
    setIsVerifying(true);
    try {
      await onComplete(sanitizedValue);
    } finally {
      verificationPending.current = false;
      setIsVerifying(false);
    }
  };
  const updateDigit = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const enteredDigits = event.target.value.replace(/\D/g, '');
    if (enteredDigits.length > 1) {
      void updateValue(enteredDigits);
      inputRefs.current[Math.min(enteredDigits.length, length) - 1]?.focus();
      return;
    }
    const digit = enteredDigits;
    const digits = Array.from({
      length
    }, (_, digitIndex) => normalizedValue.charAt(digitIndex));
    digits[index] = digit;
    void updateValue(digits.join('').slice(0, length));
    if (digit && index < length - 1) inputRefs.current[index + 1]?.focus();
  };
  const handleKeyDown = (index_0: number, event_0: KeyboardEvent<HTMLInputElement>) => {
    if (event_0.key === 'Backspace' && !normalizedValue.charAt(index_0) && index_0) {
      const previousIndex = index_0 - 1;
      event_0.preventDefault();
      void updateValue(`${normalizedValue.slice(0, previousIndex)}${normalizedValue.slice(index_0)}`);
      inputRefs.current[previousIndex]?.focus();
      return;
    }
    if (event_0.key === 'ArrowLeft' && index_0) {
      event_0.preventDefault();
      inputRefs.current[index_0 - 1]?.focus();
    }
    if (event_0.key === 'ArrowRight' && index_0 < length - 1) {
      event_0.preventDefault();
      inputRefs.current[index_0 + 1]?.focus();
    }
  };
  const handlePaste = (event_1: ClipboardEvent<HTMLInputElement>) => {
    const pastedCode = sanitizeCode(event_1.clipboardData.getData('text'), length);
    if (!pastedCode) return;
    event_1.preventDefault();
    void updateValue(pastedCode);
    inputRefs.current[Math.min(pastedCode.length, length) - 1]?.focus();
  };
  return <fieldset className="grid gap-1.5" aria-label="Verification code">
      <legend className="sr-only">Verification code</legend>
      <div className="flex justify-center gap-2">
        {Array.from({
        length
      }, (__0, index_1) => <input key={index_1} ref={element => {
        inputRefs.current[index_1] = element;
      }} aria-label={`Verification code digit ${index_1 + 1}`} autoComplete={index_1 === 0 ? 'one-time-code' : 'off'} className="h-11 w-10 rounded-md border border-input bg-transparent text-center text-lg shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50" disabled={isDisabled} inputMode="numeric" maxLength={length} pattern="[0-9]" type="text" value={normalizedValue.charAt(index_1)} onChange={event_2 => updateDigit(index_1, event_2)} onFocus={event_3 => event_3.target.select()} onKeyDown={event_4 => handleKeyDown(index_1, event_4)} onPaste={handlePaste} />)}
      </div>
      {isVerifying ? <p role="status" className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Verifying...
        </p> : null}
    </fieldset>;
};